import { Guid } from "client_legacy/ndaw/guid";
import { DocumentStoreType, NewDocumentType } from "../common";
import { IDocumentStoreSettingsModel } from "../documents";
import { SyncClient, TemplateAvailabilityKind } from "client/Api/SharePointSettings/sharepointSettings";

export enum DocumentItemType {
	Folder = 1,
	File = 2,
	OneNoteNotebook = 3
};

export enum DocumentsViewType {
	EditableView = 1,
	RecycleBinView = 3,
	SharedWithMeView = 4,
	RecentlyModified = 5
};

export enum LoadingState {
	NotLoaded = 0,
	Loaded = 1
}

export type NotLoadedExtendedInfo<_T> = {
	state: LoadingState.NotLoaded;
};

export type LoadedExtendedInfo<T> = {
	state: LoadingState.Loaded;
	info: T;
};

export type ExtendedInfo<T> = NotLoadedExtendedInfo<T> | LoadedExtendedInfo<T>;

export type DocumentsViewModel = {
	id: string;
	location: ListLocationViewModel;
	title: string;
	isPersonal: boolean;
	type: DocumentsViewType;
	path: string;
	absoluteUrl: string;
	supportsMovingFolders: boolean;
	isVersioningEnabled: boolean;
};

export type ViewAndDocumentItemModel = {
	view: DocumentsViewModel;
	documentItem: DocumentItemViewModel | null;
};

export type Permissions = {
	canView: boolean;
	canEdit: boolean;
	canCreate: boolean;
	canDelete: boolean;
	canDownload: boolean;
	canManagePermissions: boolean;
	canEnumeratePermissions: boolean;
};

export type ListLocationViewModel = {
	siteUrl: string;
	listId: Guid;
};

export type SharePointColumnsVisibilityModel = {
	columns: SharePointColumnModel[];
};

export type SharePointColumnModel = {
	id: SharePointColumnId;
	isVisible: boolean;
	isVisibleByDefault: boolean;
	cannotBeHidden: boolean;
};

export type DocumentItemViewModelBase = {
	id: number;
	name: string;
	parentFolderId: number | null;
	itemType: DocumentItemType;
	createdOn: string;
	modifiedOn: string;
	viewId: string;
	serverRelativeUrl: string;
	createdBy: string;
	modifiedBy: string;
	extension: string;
	location: ListLocationViewModel;
	permissions: Permissions;
	bookmarkId: number | null;
};

export type FileViewModel = DocumentItemViewModelBase & {
	itemType: DocumentItemType.File;
	size: number;
	checkInComment: string | null;
	labels: string | null;
	labelSetting: number | null;
	labelAppliedDateTime: string | null;
	labelAppliedBy: number | null;
	isInfected: boolean | null;
};

export type FolderViewModel = DocumentItemViewModelBase & {
	itemType: DocumentItemType.Folder;
};

export type OneNoteNotebookViewModel = DocumentItemViewModelBase & {
	itemType: DocumentItemType.OneNoteNotebook;
};

export type DocumentItemViewModel = FileViewModel | FolderViewModel | OneNoteNotebookViewModel;

export type ListLocationModel = {
	siteUrl: string;
	listId: Guid;
};

export type DocumentsRecycleBinItem = {
	guid: Guid;
	viewId: string;
	location: ListLocationViewModel;
	name: string;
	itemType: DocumentItemType;
	originalLocation: string;
	deletedOn: string;
	size: number;
};

export enum PagePositionType {
	CamlQueryPosition = 1,
	KeywordQueryPosition = 2
}

export type CamlQueryPagePosition = {
	paginInfo: string;
};

export type KeywordQueryPagePosition = {
	startRow: number;
};

export type PagePosition = {
	positionType: PagePositionType;
	camlQueryPagePosition: CamlQueryPagePosition;
	keywordQueryPosition: KeywordQueryPagePosition;
};

export enum SearchScope {
	CurrentFolder = 1,
	Subfolders = 2
}

export enum ListSortDirection {
	Ascending = 0,
	Descending = 1
}

export enum FileTypeFilterKind {
	OfficeDocument = 1,
	ExtensionList = 2
}

export type FileTypeFilterModel = {
	kind: FileTypeFilterKind.OfficeDocument;
	officeDocumentKind: DocumentKind;
} | {
	kind: FileTypeFilterKind.ExtensionList;
	extensions: readonly string[];
};

export type QueryParametersModel = {
	itemsCount: number;
	sortingField: SharePointColumnId;
	sortingDirection: ListSortDirection;
	pagePosition: PagePosition | null;
	filterText: string;
	searchScope: SearchScope | null;
	fileTypeFilter: FileTypeFilterModel | null;
};

export type RootItemsPageRequest = {
	location: ListLocationModel;
	queryParameters: QueryParametersModel;
};

export type FolderItemsPageRequest = {
	id: number;
	folderLocation: ListLocationModel;
	serverRelativeUrl: string;
	queryParameters: QueryParametersModel;
	retrieveFolderPermissions?: boolean;
};

export type GridFolderContentsModel = {
	items: DocumentItemViewModel[];
	pagerParameters: DocumentsGridPagerParameters;
	permissions: Permissions;
};

export type DocumentsGridPagerParameters = {
	totalCount: number;
	previousPagePosition: PagePosition | null;
	nextPagePosition: PagePosition | null;
};

export type DocumentsErrorModel = {
	field: string;
} & (
	{
		message: string;
	}
	|
	{
		messageTitle: string;
		messageBody: string;
	}
);

export type DocumentTemplateModel = {
	name: string;
	url: string;
	resultingExtension: string;
	documentKind: NewDocumentType;
};

export const enum DocumentsErrorMessageType {
	None = 0,
	DocumentsViewNotFound = 4
}

export type DocumentsErrorMessage = {
	errorMessageType: DocumentsErrorMessageType;
	header: string;
	text: string;
};

export const enum SharePointColumnId {
	name = 1,
	dateCreated = 2,
	dateModified = 3,
	createdBy = 4,
	modifiedBy = 5,
	extension = 6,
	fileSize = 7,
	id = 8,
	checkInComment = 9,
	labels = 10,
	labelSetting = 11,
	labelApplied = 12,
	labelAppliedBy = 13,
	path = 14
}

export enum DocumentKind {
	Word = 1,
	Excel = 2,
	PowerPoint = 3,
	OneNote = 4,
	Visio = 5
}

export enum OfficeWebAppOpenTarget {
	Popup = 0,
	NewWorkspaceTab = 1,
	NewOffice365Tab = 2
}

export interface SharePointDocumentsSettingsModel extends IDocumentStoreSettingsModel {
	storeType: DocumentStoreType.SharePoint;
	isSharingViaLinkEnabled: boolean;
	isDocumentViewStructureCustomizationEnabled: boolean;
	syncClient: SyncClient;
	templateAvailabilityKind: TemplateAvailabilityKind;
	columnsVisibility: SharePointColumnsVisibilityModel;
}

export type RecentDocumentItemViewModel = {
	itemType: DocumentItemType.File | DocumentItemType.OneNoteNotebook;
	id: number;
	listId: string;
	name: string;
	modifiedOn: string | null;
	modifiedBy: string | null;
	extension: string | null;
	location: ListLocationViewModel;
	serverRelativeUrl: string;
	isShared: boolean;
	createdBy: string | null;
	size: number | null;
	bookmarkId: number | null;
};